import { options, Topics, topics } from "@/components/one-to-one/Constants.tsx";
import DesktopSecondSessionView from "@/components/one-to-one/DesktopSecondSessionView.tsx";
import DesktopView from "@/components/one-to-one/DesktopView.tsx";
import MobileView from "@/components/one-to-one/MobileView.tsx";
import PageHeading from "@/components/ui/PageHeading.tsx";
import routes from "@/config/routes.ts";
import { useSEO } from "@/contexts/SeoContext.tsx";
import {
  useOneToOneTopics,
  useTutorOneToOneAvailabilities,
} from "@/data/one-to-one.ts";
import { useMe } from "@/data/user.ts";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";
import { InfoPreviousTutorOneToOne, InfoTutorOneToOne } from "@/types";
import { useBreakpoint } from "@/utils/hooks/useBreakpoints.ts";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import useOneToOne from "@/utils/hooks/useOneToOne.ts";
import cn from "classnames";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const STORAGE_PATH = import.meta.env.EDS_STORAGE_URL;

export interface AllTutorsObj {
  name: string;
  image: string;
  token: string;
  date: string;
}

export interface AllTutorsHourObj {
  hour: string;
  tutorsAvailable: AllTutorsObj[];
}

export default function OneToOne() {
  const { me } = useMe();

  const { setTitle, setDescription } = useSEO();

  const breakpoint = useBreakpoint();
  const isSmallScreen = ["xs", "sm", "md", "lg"].indexOf(breakpoint) !== -1;

  const [topicsDone, setTopicsDone] = useState<string[] | null>(null);
  const [secondSession, setSecondSession] = useState<boolean>(false);
  const [allDates, setAllDates] = useState<string[]>([]);
  const [allTutorDates, setAllTutorDates] = useState<AllTutorsHourObj[]>([]);
  const [previousTutorDates, setPreviousTutorDates] =
    useState<InfoPreviousTutorOneToOne>();

  const {
    date,
    // hour,
    category,
    oneToOneDone,
    credits: stateCredits,
    setState,
  } = useOneToOne();

  // const dayOfMeeting = dayjs(date)?.format("ddd DD MMM");

  const { setGlobalState } = useGlobals();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { oneToOneTopicsDone } = useOneToOneTopics();
  const { availabilities, isLoading } = useTutorOneToOneAvailabilities(
    {
      tutor_role: "One to one",
      half_time: false,
      time_distance: 12,
    },
    // disable,
    false,
  );

  const availableCalendarDays = useMemo(() => {
    const temp: dayjs.Dayjs[] = [];

    allDates.forEach((el) => {
      const tempDate = dayjs(el.split("/")[0]);
      if (!temp.includes(tempDate) && !dayjs(tempDate).isBefore(dayjs())) {
        temp.push(tempDate);
      }
    });

    // if (!date) {
    setState("date", temp[0]);
    // }

    return temp;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDates]);

  useEffect(() => {
    if (!oneToOneTopicsDone) return;

    setState("oneToOneDone", oneToOneTopicsDone.item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneToOneTopicsDone]);

  const selectedDayEvents = useMemo(
    () =>
      allTutorDates
        .filter(
          (e) =>
            dayjs(e.hour).isSame(date, "day") &&
            !dayjs(e.hour).isBefore(dayjs()),
        )
        .map((el) => ({
          hour: el.hour.split(" ")[1],
          tutorsAvailable: el.tutorsAvailable,
        }))
        .sort((a, b) => {
          // Convert hour strings to numbers for comparison
          const hourA = parseInt(a.hour.split(":")[0]);
          const hourB = parseInt(b.hour.split(":")[0]);

          // Compare hours
          if (hourA < hourB) return -1;
          if (hourA > hourB) return 1;
          return 0;
        }),
    [allTutorDates, date],
  );

  const selectedPreviousTutorDayEvents = useMemo(
    () =>
      previousTutorDates?.available_dates
        .filter(
          (e) => dayjs(e).isSame(date, "day") && !dayjs(e).isBefore(dayjs()),
        )
        .map((el) => el.split(" ")[1]),
    [previousTutorDates, date],
  );

  useEffect(() => {
    if (["A0", "A0-"].includes(me?.item.user.english_level ?? "")) {
      navigate(routes.agenda, { replace: true });
    }

    setTitle(
      `${me?.item.user.first_name} ${me?.item.user.last_name} | One To One`,
    );
    setDescription("One To One Page");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  useEffect(() => {
    if (!availabilities) return;
    if (!availabilities.item) return;

    const arrayDates: string[] = [];
    const allList: AllTutorsObj[] = [];

    const previousTutor =
      availabilities.item.current_tutor ?? availabilities.item.tutors[0];

    availabilities.item.tutors.forEach((element: InfoTutorOneToOne) => {
      element.available_dates.forEach((el) => {
        arrayDates.push(el + "/" + element.tutor.token);

        const currentInfo = {
          name: element.tutor.first_name,
          image: element.tutor.profile_pic,
          token: element.tutor.token,
          date: el,
        };

        allList.push(currentInfo);
      });
    });

    const groupedByDate: Record<string, AllTutorsObj[]> = {};

    // Raggruppa gli oggetti per data
    allList?.forEach((tutor) => {
      const currentDate: string = tutor.date;

      if (!groupedByDate[currentDate]) {
        groupedByDate[currentDate] = [];
      }

      groupedByDate[currentDate].push({
        name: tutor.name,
        image: STORAGE_PATH + "/" + tutor.image,
        token: tutor.token,
        date: currentDate,
      });
    });

    // Formatta l'output richiesto
    const formattedResult = Object.keys(groupedByDate).map((currentDate) => ({
      hour: currentDate,
      tutorsAvailable: groupedByDate[currentDate],
    }));

    setAllTutorDates(formattedResult);

    setAllDates(arrayDates);

    if (!previousTutor) return;

    setPreviousTutorDates({
      available_dates:
        availabilities.item?.current_tutor?.available_dates ?? [],
      tutor: {
        name: previousTutor.tutor.first_name,
        image: STORAGE_PATH + "/" + previousTutor.tutor.profile_pic,
        token: previousTutor.tutor.token,
      },
    });
  }, [availabilities]);

  useEffect(() => {
    const allTopicsDone: string[] = [];

    const totalTopicsLength = Object.values(topics).reduce((total, arr) => {
      return total + arr.length;
    }, 0);

    setState("freeTopic", totalTopicsLength <= oneToOneDone.length);

    topics[options[Number(category)]?.key as keyof Topics]?.forEach(
      (element: { title: string }) => {
        const topicDone = oneToOneDone
          .filter((el) => el.descrizione === element.title)
          .map((el) => el.descrizione)[0];

        if (topicDone) allTopicsDone.push(topicDone);
      },
    );

    setTopicsDone(allTopicsDone);
  }, [category, oneToOneDone]);

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-brand-background");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const PageHeadingComponent = () => (
    <PageHeading
      title={t("menu.one_to_one")}
      subtitle={t("one_to_one.subtitle")}
      className={"until-lg:px-4 until-lg:pb-0"}
      button={
        <div
          className={"flex-row hidden lg:flex  w-[304px]  justify-end"}
          id="creditsDiv"
        >
          <div
            className={cn(
              " rounded-md py-3  w-full bg-brand-lavender text-center",
              {
                "border-solid border-light bg-brand-light border ":
                  stateCredits === 0,
              },
            )}
          >
            <p className={"text-base font-medium text-primary"}>
              {t("one_to_one.your_credits")}:&nbsp;
              <strong
                className={cn({
                  "!text-red-600": stateCredits === 0,
                  "bounce-once": Boolean(stateCredits),
                })}
              >
                {stateCredits}
              </strong>
            </p>
          </div>
        </div>
      }
    />
  );

  return (
    <DashboardContentLayout className={"until-lg:pb-0 until-lg:px-0"}>
      {me?.item.user.english_level != "A0" && (
        <>
          {(() => {
            if (isSmallScreen) {
              return (
                <MobileView
                  availableCalendarDays={availableCalendarDays}
                  selectedDayEvents={selectedDayEvents}
                  selectedPreviousTutorDayEvents={
                    selectedPreviousTutorDayEvents || []
                  }
                  previousTutorInfo={previousTutorDates?.tutor}
                  isLoading={isLoading}
                />
              );
            } else if (secondSession) {
              return (
                <>
                  <PageHeadingComponent />
                  <DesktopSecondSessionView
                    topicsDone={topicsDone}
                    setSecondSession={setSecondSession}
                    credits={stateCredits}
                  />
                </>
              );
            } else {
              return (
                <>
                  <PageHeadingComponent />
                  <DesktopView
                    availableCalendarDays={availableCalendarDays}
                    selectedDayEvents={selectedDayEvents}
                    selectedPreviousTutorDayEvents={
                      selectedPreviousTutorDayEvents || []
                    }
                    previousTutorInfo={previousTutorDates?.tutor}
                    setSecondSession={setSecondSession}
                    isLoading={isLoading}
                  />
                </>
              );
            }
          })()}
        </>
      )}
    </DashboardContentLayout>
  );
}
